<template>
  <div class="purchase-list-page">
    <!-- 顶部导航 -->
    <header class="back-header">
      <div class="back-btn" @click="goBack">←</div>
      <h1>{{ $t("wholesaler.PurchaseList.title") }}</h1>
      <div class="header-actions">
        <button class="add-btn" @click="goToCreate">+ {{ $t("wholesaler.PurchaseList.create") }}</button>
      </div>
    </header>

    <div class="main-content">
      <!-- 统计卡片 -->
      <div class="stats-cards">
        <div class="stat-card">
          <div class="stat-label">{{ $t("wholesaler.PurchaseList.monthlyOrders") }}</div>
          <div class="stat-value">{{ stats.monthlyOrders || 0 }}{{ $t("wholesaler.PurchaseList.unit") }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">{{ $t("wholesaler.PurchaseList.pendingReceive") }}</div>
          <div class="stat-value warning">{{ stats.pendingReceiveOrders || 0 }}{{ $t("wholesaler.PurchaseList.unit") }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">{{ $t("wholesaler.PurchaseList.pendingPayment") }}</div>
          <div class="stat-value danger">{{ stats.unpaidOrders || 0 }}{{ $t("wholesaler.PurchaseList.unit") }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">{{ $t("wholesaler.PurchaseList.monthlyAmount") }}</div>
          <div class="stat-value">¥{{ formatMoney(stats.monthlyAmount) }}</div>
        </div>
      </div>

      <!-- 筛选栏 -->
      <div class="filter-bar">
        <select v-model="filter.status" @change="loadData" class="filter-select">
          <option value="">{{ $t("wholesaler.PurchaseList.allStatus") }}</option>
          <option value="DRAFT">{{ $t("wholesaler.PurchaseList.statusDraft") }}</option>
          <option value="CONFIRMED">{{ $t("wholesaler.PurchaseList.statusConfirmed") }}</option>
          <option value="RECEIVED">{{ $t("wholesaler.PurchaseList.statusReceived") }}</option>
          <option value="CANCELLED">{{ $t("wholesaler.PurchaseList.statusCancelled") }}</option>
        </select>
        <select v-model="filter.supplierId" @change="loadData" class="filter-select">
          <option value="">{{ $t("wholesaler.PurchaseList.allSuppliers") }}</option>
          <option v-for="s in suppliers" :key="s.id" :value="s.id">{{ s.name }}</option>
        </select>
        <input type="date" v-model="filter.startDate" @change="loadData" class="filter-date" />
        <input type="date" v-model="filter.endDate" @change="loadData" class="filter-date" />
      </div>

      <!-- 进货单列表 -->
      <div class="purchase-list">
        <div v-for="order in orders" :key="order.id" class="purchase-card" @click="goToDetail(order.id)">
          <div class="card-header">
            <span class="order-no">{{ order.purchaseNo }}</span>
            <span class="status-tag" :class="getStatusClass(order.status)">{{ order.statusLabel }}</span>
          </div>
          <div class="card-body">
            <div class="info-row">
              <span class="label">{{ $t("wholesaler.PurchaseList.supplier") }}:</span>
              <span class="value">{{ order.supplierName || '-' }}</span>
            </div>
            <div class="info-row">
              <span class="label">{{ $t("wholesaler.PurchaseList.warehouse") }}:</span>
              <span class="value">{{ order.warehouseName || '-' }}</span>
            </div>
            <div class="info-row products-row" v-if="order.items && order.items.length > 0">
              <span class="label">{{ $t("wholesaler.PurchaseList.product") }}:</span>
              <span class="value products-list">{{ order.items.map(i => i.productName).join('、') }}</span>
            </div>
            <div class="info-row compact-row">
              <span class="compact-item">
                <span class="label">{{ $t("wholesaler.PurchaseList.amount") }}:</span>
                <span class="value money">￥{{ formatMoney(order.totalAmount) }}</span>
              </span>
              <span class="compact-item">
                <span class="label">{{ $t("wholesaler.PurchaseList.payment") }}:</span>
                <span class="value" :class="getPaymentClass(order.paymentStatus)">
                  {{ getPaymentLabel(order.paymentStatus) }}
                  <span v-if="order.paidAmount > 0">(已付￥{{ formatMoney(order.paidAmount) }})</span>
                </span>
              </span>
              <span class="compact-item">
                <span class="label">{{ $t("wholesaler.PurchaseList.date") }}:</span>
                <span class="value">{{ order.purchaseDate }}</span>
              </span>
            </div>
          </div>
          <div class="card-actions" v-if="order.status !== 'CANCELLED'">
            <button v-if="order.status === 'DRAFT'" class="action-btn primary" @click.stop="confirmOrder(order)">确认</button>
            <button v-if="order.status === 'CONFIRMED'" class="action-btn success" @click.stop="receiveOrder(order)">收货</button>
            <button v-if="order.paymentStatus !== 'PAID'" class="action-btn warning" @click.stop="openPaymentDialog(order)">支付</button>
            <button v-if="order.status === 'RECEIVED' && order.paidAmount > 0 && !order.hasPendingReturn && !order.fullyReturned" class="action-btn after-sale" @click.stop="createReturn(order)">售后</button>
            <button v-if="order.status !== 'RECEIVED'" class="action-btn danger" @click.stop="cancelOrder(order)">取消</button>
          </div>
        </div>

        <div v-if="orders.length === 0" class="empty-state">
          <div class="empty-icon">📦</div>
          <p>{{ $t("wholesaler.PurchaseList.empty") }}</p>
          <button class="add-btn" @click="goToCreate">{{ $t("wholesaler.PurchaseList.createFirst") }}</button>
        </div>
      </div>

      <!-- 分页 -->
      <div class="pagination" v-if="total > pageSize">
        <button :disabled="page === 1" @click="changePage(page - 1)">上一页</button>
        <span>{{ page }} / {{ Math.ceil(total / pageSize) }}</span>
        <button :disabled="page >= Math.ceil(total / pageSize)" @click="changePage(page + 1)">下一页</button>
      </div>
    </div>

    <!-- 付款弹窗 -->
    <div v-if="showPaymentDialog" class="modal-overlay modal-show" @click="closePaymentDialog">
      <div class="modal-content" @click.stop>
        <h3>{{ $t("wholesaler.PurchaseList.payment") }} - {{ currentOrder.purchaseNo }}</h3>
        <div class="form-group">
          <label>{{ $t("wholesaler.PurchaseList.amountDue") }}</label>
          <div class="money-display">¥{{ formatMoney(currentOrder.totalAmount - currentOrder.paidAmount) }}</div>
        </div>
        <div class="form-group">
          <label>{{ $t("wholesaler.PurchaseList.paymentAmount") }} *</label>
          <input type="number" v-model="paymentForm.amount" step="0.01" min="0.01" class="form-input" @blur="roundAmount" />
        </div>
        <div class="form-group">
          <label>{{ $t("wholesaler.PurchaseList.paymentMethod") }}</label>
          <select v-model="paymentForm.method" class="form-select">
            <option value="CASH">{{ $t("wholesaler.PurchaseList.cash") }}</option>
            <option value="CREDIT_CARD">{{ $t("wholesaler.PurchaseList.creditCard") }}</option>
            <option value="BANK_TRANSFER">{{ $t("wholesaler.PurchaseList.transfer") }}</option>
            <option value="ONLINE_BANKING">{{ $t("wholesaler.PurchaseList.onlineBanking") }}</option>
          </select>
        </div>
        <div class="form-group">
          <label>{{ $t("wholesaler.PurchaseList.paymentProof") }} *</label>
          <input type="file" @change="uploadProof" accept="image/*" class="form-input" />
          <img v-if="paymentForm.proofUrl" :src="getImageUrl(paymentForm.proofUrl)" class="proof-preview" />
        </div>
        <div class="form-group">
          <label>{{ $t("wholesaler.PurchaseList.remark") }}</label>
          <textarea v-model="paymentForm.remark" class="form-textarea" rows="2"></textarea>
        </div>
        <div class="modal-actions">
          <button class="btn-cancel" @click="closePaymentDialog">{{ $t("wholesaler.PurchaseList.cancel") }}</button>
          <button class="btn-confirm" @click="submitPayment">{{ $t("wholesaler.PurchaseList.confirmPayment") }}</button>
        </div>
      </div>
    </div>

    <!-- 退货弹窗 -->
    <div v-if="showReturnDialog" class="modal-overlay modal-show" @click="closeReturnDialog">
      <div class="modal-content modal-large" @click.stop>
        <h3>{{ $t("wholesaler.PurchaseList.afterSalesReturn") }} - {{ returnOrder.purchaseNo }}</h3>

        <div v-if="returnLoading" class="loading-state">{{ $t("wholesaler.PurchaseList.loading") }}</div>

        <template v-else>
          <div class="return-section">
            <h4>{{ $t("wholesaler.PurchaseList.selectReturnProducts") }}</h4>
            <div class="return-items">
              <div v-for="(item, index) in returnForm.items" :key="index" class="return-item">
                <label class="checkbox-label">
                  <input type="checkbox" v-model="item.selected" @change="updateReturnTotal" />
                  <span>{{ item.productName }}</span>
                </label>
                <div class="item-details" v-if="item.selected">
                  <div class="item-info">
                    <span>{{ $t("wholesaler.PurchaseList.purchaseQuantity") }}: {{ item.quantity }}{{ item.unit }}</span>
                  </div>
                  <div class="return-quantity">
                    <label>{{ $t("wholesaler.PurchaseList.returnQuantity") }}:</label>
                    <input type="number" v-model.number="item.returnQuantity" min="0.01" :max="item.quantity" step="0.01" class="quantity-input" @change="updateReturnTotal" />
                    <span>{{ item.unit }}</span>
                  </div>
                  <div class="return-price">
                    <label>{{ $t("wholesaler.PurchaseList.unitPrice") }}:</label>
                    <input type="number" v-model.number="item.unitPrice" min="0" step="0.01" class="price-input" @change="onPriceChange(item)" />
                  </div>
                  <div class="return-subtotal">
                    <label>{{ $t("wholesaler.PurchaseList.subtotal") }}:</label>
                    <input type="number" v-model.number="item.subtotal" min="0" step="0.01" class="subtotal-input" @change="onSubtotalChange(item)" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="return-section">
            <h4>{{ $t("wholesaler.PurchaseList.returnReason") }} *</h4>
            <select v-model="returnForm.reasonCode" class="form-select">
              <option value="">{{ $t("wholesaler.PurchaseList.selectReason") }}</option>
              <option value="QUALITY_ISSUE">{{ $t("wholesaler.PurchaseList.qualityIssue") }}</option>
              <option value="WRONG_ITEM">{{ $t("wholesaler.PurchaseList.wrongItem") }}</option>
              <option value="WRONG_QUANTITY">{{ $t("wholesaler.PurchaseList.wrongQuantity") }}</option>
              <option value="DAMAGED">{{ $t("wholesaler.PurchaseList.damaged") }}</option>
              <option value="EXPIRED">{{ $t("wholesaler.PurchaseList.expired") }}</option>
              <option value="SPEC_MISMATCH">{{ $t("wholesaler.PurchaseList.specMismatch") }}</option>
              <option value="OTHER">{{ $t("wholesaler.PurchaseList.other") }}</option>
            </select>
          </div>

          <div class="return-section">
            <h4>{{ $t("wholesaler.PurchaseList.returnDescription") }} *</h4>
            <textarea v-model="returnForm.reasonText" class="form-textarea" rows="3" :placeholder="$t('wholesaler.PurchaseList.describeReturnReason')"></textarea>
          </div>

          <div class="return-section">
            <h4>{{ $t("wholesaler.PurchaseList.uploadProof") }}</h4>
            <input type="file" @change="uploadReturnProof" accept="image/*" multiple class="form-input" />
            <div class="proof-list" v-if="returnForm.evidenceUrls.length > 0">
              <img v-for="(url, index) in returnForm.evidenceUrls" :key="index" :src="getImageUrl(url)" class="proof-thumb" />
            </div>
          </div>

          <div class="return-section">
            <h4>{{ $t("wholesaler.PurchaseList.refundInfo") }}</h4>
            <div class="refund-info">
              <div class="info-row">
                <span>{{ $t("wholesaler.PurchaseList.returnProductAmount") }}:</span>
                <span>¥{{ formatMoney(returnForm.totalAmount) }}</span>
              </div>
              <div class="info-row">
                <span>{{ $t("wholesaler.PurchaseList.refundAmount") }}:</span>
                <input type="number" v-model.number="returnForm.totalRefundAmount" step="0.01" min="0" class="refund-amount-input" />
              </div>
              <div class="info-row">
                <label class="checkbox-label">
                  <input type="checkbox" v-model="returnForm.refundReceived" />
                  <span>{{ $t("wholesaler.PurchaseList.receivedRefund") }}</span>
                </label>
              </div>
              <template v-if="returnForm.refundReceived">
                <div class="info-row">
                  <span>{{ $t("wholesaler.PurchaseList.refundType") }}:</span>
                  <select v-model="returnForm.refundType" class="form-select">
                    <option value="ORIGINAL">{{ $t("wholesaler.PurchaseList.originalReturn") }}</option>
                    <option value="CASH">{{ $t("wholesaler.PurchaseList.cash") }}</option>
                    <option value="CREDIT_CARD">{{ $t("wholesaler.PurchaseList.creditCard") }}</option>
                    <option value="BANK_TRANSFER">{{ $t("wholesaler.PurchaseList.transfer") }}</option>
                    <option value="PAYNOW">PayNow</option>
                  </select>
                </div>
                <div class="info-row">
                  <span>{{ $t("wholesaler.PurchaseList.receivedAmount") }}:</span>
                  <input type="number" v-model.number="returnForm.refundAmount" step="0.01" min="0" class="refund-amount-input" />
                </div>
                <div class="info-row">
                  <span>{{ $t("wholesaler.PurchaseList.receivedProof") }}:</span>
                  <input type="file" @change="uploadRefundProof" accept="image/*" class="form-input" />
                </div>
                <div class="proof-list" v-if="returnForm.refundProofUrl">
                  <img :src="getImageUrl(returnForm.refundProofUrl)" class="proof-thumb" />
                </div>
              </template>
            </div>
          </div>

          <div class="modal-actions">
            <button class="btn-cancel" @click="closeReturnDialog">{{ $t("wholesaler.PurchaseList.cancel") }}</button>
            <button class="btn-confirm" @click="submitReturn" :disabled="!canSubmitReturn">{{ $t("wholesaler.PurchaseList.submitReturnApplication") }}</button>
          </div>
        </template>
      </div>
    </div>
    <!-- 底部导航 -->
    <nav class="bottom-nav">
      <button class="nav-item" @click="navigateTo('home')">
        <span class="nav-item-icon">🏠</span>
        <span>{{ $t("wholesaler.PurchaseList.home") }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('products')">
        <span class="nav-item-icon">📦</span>
        <span>{{ $t("wholesaler.PurchaseList.products") }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('wholesale')">
        <span class="nav-item-icon">💰</span>
        <span>{{ $t("wholesaler.PurchaseList.wholesalePrice") }}</span>
      </button>
      <button class="nav-item active">
        <span class="nav-item-icon">📋</span>
        <span>{{ $t("wholesaler.PurchaseList.orders") }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('profile')">
        <span class="nav-item-icon">👤</span>
        <span>{{ $t("wholesaler.PurchaseList.profile") }}</span>
      </button>
    </nav>
  </div>
</template>

<script>
import { getImageUrl } from '@/utils/image';
import {
  getPurchaseOrderList,
  getPurchaseOrderDetail,
  getPurchaseStats,
  getActiveSuppliers,
  confirmPurchaseOrder,
  receivePurchaseOrder,
  cancelPurchaseOrder,
  payPurchaseOrder
} from '@/api/purchase'
import { createPurchaseReturn } from '@/api/purchaseReturn'
import { getWholesalerId } from '@/utils/wholesaler'

export default {
  name: 'PurchaseList',

  data() {
    return {
      stats: {},
      suppliers: [],
      orders: [],
      filter: {
        status: '',
        supplierId: '',
        startDate: '',
        endDate: ''
      },
      page: 1,
      pageSize: 20,
      total: 0,
      showPaymentDialog: false,
      currentOrder: {},
      paymentForm: {
        amount: 0,
        method: 'CASH',
        remark: '',
        proofUrl: ''
      },
      showReturnDialog: false,
      returnOrder: null,
      returnLoading: false,
      returnForm: {
        items: [],
        reasonCode: '',
        reasonText: '',
        evidenceUrls: [],
        totalAmount: 0,
        gstAmount: 0,
        totalRefundAmount: 0,
        refundReceived: true,
        refundType: 'ORIGINAL',
        refundAmount: 0,
        refundProofUrl: ''
      }
    }
  },

  computed: {
    wholesalerId() {
      return getWholesalerId()
    },
    canSubmitReturn() {
      const hasSelected = this.returnForm.items.some(item => item.selected && item.returnQuantity > 0)
      return hasSelected && this.returnForm.reasonCode && this.returnForm.reasonText
    }
  },

  created() {
    this.loadData()
    this.loadStats()
    this.loadSuppliers()
  },

  methods: {
    getImageUrl,
    async loadData() {
      try {
        const wholesalerId = this.wholesalerId || 1
        console.log('加载进货单列表,wholesalerId:', wholesalerId)
        const params = {
          wholesalerId: wholesalerId,
          page: this.page,
          size: this.pageSize
        }
        if (this.filter.status) params.status = this.filter.status
        if (this.filter.supplierId) params.supplierId = this.filter.supplierId
        if (this.filter.startDate) params.startDate = this.filter.startDate
        if (this.filter.endDate) params.endDate = this.filter.endDate

        const res = await getPurchaseOrderList(params)
        this.orders = res.content || []
        this.total = res.totalElements || 0
      } catch (e) {
        console.error('加载进货单失败', e)
        this.$toast.error('加载失败')
      }
    },

    async loadStats() {
      try {
        const wholesalerId = this.wholesalerId || 1
        const res = await getPurchaseStats(wholesalerId)
        this.stats = res || {}
      } catch (e) {
        console.error('加载统计失败', e)
      }
    },

    async loadSuppliers() {
      try {
        const wholesalerId = this.wholesalerId || 1
        const res = await getActiveSuppliers(wholesalerId)
        this.suppliers = res || []
      } catch (e) {
        console.error('加载供应商失败', e)
      }
    },

    goToCreate() {
      this.$router.push('/wholesaler/purchase-create')
    },

    goToDetail(id) {
      this.$router.push(`/wholesaler/purchase-detail/${id}`)
    },

    navigateTo(tab) {
      const routes = {
        home: '/wholesaler/home',
        products: '/wholesaler/product',
        wholesale: '/wholesaler/wholesale-price-list',
        orders: '/wholesaler/orders',
        profile: '/my-profile'
      }
      if (routes[tab]) {
        this.$router.push(routes[tab])
      }
    },
    goBack() {
      this.$router.go(-1)
    },

    async confirmOrder(order) {
      if (!confirm(`确认进货单 ${order.purchaseNo}?`)) return
      try {
        await confirmPurchaseOrder(order.id, this.wholesalerId)
        this.$toast.success('已确认')
        this.loadData()
        this.loadStats()
      } catch (e) {
        this.$toast.error('操作失败')
      }
    },

    async receiveOrder(order) {
      if (!confirm(`确认收货 ${order.purchaseNo}?收货后库存将自动增加。`)) return
      try {
        const userId = localStorage.getItem('user_id')
        await receivePurchaseOrder(order.id, this.wholesalerId, userId)
        this.$toast.success('收货成功,库存已更新')
        this.loadData()
        this.loadStats()
      } catch (e) {
        this.$toast.error('操作失败')
      }
    },

    async cancelOrder(order) {
      if (!confirm(`确认取消进货单 ${order.purchaseNo}?`)) return
      try {
        await cancelPurchaseOrder(order.id, this.wholesalerId)
        this.$toast.success('已取消')
        this.loadData()
        this.loadStats()
      } catch (e) {
        this.$toast.error('操作失败')
      }
    },

    openPaymentDialog(order) {
      this.currentOrder = order
      this.paymentForm.amount = Math.round((order.totalAmount - order.paidAmount) * 100) / 100
      this.paymentForm.method = 'CASH'
      this.paymentForm.remark = ''
      this.paymentForm.proofUrl = ''
      this.showPaymentDialog = true
      console.log('打开支付弹窗:', order.purchaseNo, '待付金额:', this.paymentForm.amount)
    },
    roundAmount() {
      if (this.paymentForm.amount) {
        this.paymentForm.amount = Math.round(this.paymentForm.amount * 100) / 100
      }
    },

    closePaymentDialog() {
      this.showPaymentDialog = false
    },

    async submitPayment() {
      if (!this.paymentForm.amount || this.paymentForm.amount <= 0) {
        this.$toast.error('请输入支付金额')
        return
      }
      if (!this.paymentForm.proofUrl) {
        this.$toast.error('请上传支付凭证')
        return
      }
      try {
        const operatorId = localStorage.getItem('user_id')
        await payPurchaseOrder(this.currentOrder.id, {
          amount: this.paymentForm.amount,
          paymentMethod: this.paymentForm.method,
          remark: this.paymentForm.remark,
          proofUrl: this.paymentForm.proofUrl
        }, this.wholesalerId, operatorId)
        this.$toast.success('支付成功')
        this.closePaymentDialog()
        this.loadData()
        this.loadStats()
      } catch (e) {
        console.error('支付失败', e)
        this.$toast.error('支付失败: ' + (e.response?.data?.message || e.message))
      }
    },

    async uploadProof(e) {
      const file = e.target.files[0]
      if (!file) return
      try {
        const { uploadFile } = require('@/services/api')
        const res = await uploadFile('/files/upload-image', file)
        this.paymentForm.proofUrl = res.url
        this.$toast.success('凭证上传成功')
      } catch (err) {
        console.error('上传失败', err)
        this.$toast.error('上传失败: ' + err.message)
      }
    },

    changePage(p) {
      this.page = p
      this.loadData()
    },

    formatMoney(value) {
      if (!value) return '0.00'
      return Number(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    getStatusClass(status) {
      const map = {
        DRAFT: 'draft',
        CONFIRMED: 'pending',
        RECEIVED: 'success',
        CANCELLED: 'cancelled'
      }
      return map[status] || ''
    },

    getPaymentClass(status) {
      if (status === 'PAID') return 'paid'
      if (status === 'PARTIAL') return 'partial'
      return 'unpaid'
    },

    getPaymentLabel(status) {
      const map = { UNPAID: this.$t('wholesaler.PurchaseList.unpaid'), PARTIAL: this.$t('wholesaler.PurchaseList.partial'), PAID: this.$t('wholesaler.PurchaseList.paid') }
      return map[status] || status
    },

    async createReturn(order) {
      this.returnOrder = order
      this.returnLoading = true
      this.showReturnDialog = true
      // 加载进货单详情获取商品明细
      try {
        const detail = await getPurchaseOrderDetail(order.id, this.wholesalerId)
        const items = (detail.items || []).map(item => ({
          ...item,
          selected: true,
          returnQuantity: item.quantity,
          unitPrice: item.unitPrice,
          subtotal: item.quantity * item.unitPrice
        }))
        const totalAmount = items.reduce((sum, item) => sum + item.subtotal, 0)
        this.returnForm = {
          items: items,
          reasonCode: '',
          reasonText: '',
          evidenceUrls: [],
          totalAmount: totalAmount,
          gstAmount: 0,
          totalRefundAmount: totalAmount,
          refundReceived: true,
          refundType: 'ORIGINAL',
          refundAmount: totalAmount,
          refundProofUrl: ''
        }
      } catch (e) {
        console.error('加载进货单明细失败', e)
        this.$toast.error('加载商品明细失败')
      } finally {
        this.returnLoading = false
      }
    },

    closeReturnDialog() {
      this.showReturnDialog = false
      this.returnOrder = null
    },

    updateReturnTotal() {
      let total = 0
      this.returnForm.items.forEach(item => {
        if (item.selected && item.returnQuantity > 0) {
          item.subtotal = Math.round(item.returnQuantity * item.unitPrice * 100) / 100
          total += item.subtotal
        }
      })
      this.returnForm.totalAmount = total
      this.returnForm.totalRefundAmount = total
      this.returnForm.refundAmount = total
    },

    onPriceChange(item) {
      if (item.returnQuantity > 0 && item.unitPrice >= 0) {
        item.subtotal = Math.round(item.returnQuantity * item.unitPrice * 100) / 100
        this.updateReturnTotal()
      }
    },

    onSubtotalChange(item) {
      if (item.returnQuantity > 0 && item.subtotal >= 0) {
        item.unitPrice = Math.round((item.subtotal / item.returnQuantity) * 100) / 100
        this.updateReturnTotal()
      }
    },

    async uploadReturnProof(e) {
      const files = Array.from(e.target.files)
      if (!files || files.length === 0) return
      try {
        const { uploadFile } = require('@/services/api')
        for (const file of files) {
          const res = await uploadFile('/files/upload-image', file)
          this.returnForm.evidenceUrls.push(res.url)
        }
        this.$toast.success('凭证上传成功')
      } catch (err) {
        console.error('上传失败', err)
        this.$toast.error('上传失败')
      }
    },

    async uploadRefundProof(e) {
      const file = e.target.files[0]
      if (!file) return
      try {
        const { uploadFile } = require('@/services/api')
        const res = await uploadFile('/files/upload-image', file)
        this.returnForm.refundProofUrl = res.url
        this.$toast.success('收款凭证上传成功')
      } catch (err) {
        console.error('上传失败', err)
        this.$toast.error('上传失败: ' + err.message)
      }
    },

    async submitReturn() {
      const selectedItems = this.returnForm.items.filter(item => item.selected && item.returnQuantity > 0)
      if (selectedItems.length === 0) {
        this.$toast.error('请选择退货商品')
        return
      }
      if (!this.returnForm.reasonCode) {
        this.$toast.error('请选择退货原因')
        return
      }
      if (!this.returnForm.reasonText) {
        this.$toast.error('请填写退货说明')
        return
      }
      try {
        const data = {
          purchaseId: this.returnOrder.id,
          reasonCode: this.returnForm.reasonCode,
          reasonText: this.returnForm.reasonText,
          evidenceUrls: JSON.stringify(this.returnForm.evidenceUrls),
          totalRefundAmount: this.returnForm.totalRefundAmount,
          items: selectedItems.map(item => ({
            purchaseItemId: item.id,
            productId: item.productId,
            returnQuantity: item.returnQuantity,
            unitPrice: item.unitPrice,
            subtotal: item.subtotal
          }))
        }
        // 已收到退款，附带退款流水信息
        if (this.returnForm.refundReceived) {
          data.refundReceived = true
          data.refundType = this.returnForm.refundType
          data.refundAmount = this.returnForm.refundAmount || this.returnForm.totalRefundAmount
          data.refundProofUrl = this.returnForm.refundProofUrl
        }
        const userId = localStorage.getItem('user_id')
        await createPurchaseReturn(data, this.wholesalerId, userId)
        this.$toast.success('退货申请提交成功')
        this.closeReturnDialog()
        this.loadData()
        this.loadStats()
      } catch (e) {
        console.error('提交退货失败', e)
        this.$toast.error('提交失败: ' + (e.response?.data?.message || e.message))
      }
    }
  }
}
</script>

<style scoped>
.purchase-list-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 70px;
}

/* 底部导航 */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: space-around;
  z-index: 100;
  height: 56px;
}

.nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #999;
  font-size: 11px;
  border: none;
  background: none;
  transition: color 0.3s;
}

.nav-item.active {
  color: #667eea;
}

.nav-item-icon {
  font-size: 24px;
  margin-bottom: 2px;
}

.back-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: white;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  z-index: 100;
}

.back-btn {
  font-size: 20px;
  cursor: pointer;
  padding: 4px 8px;
}

.back-header h1 {
  flex: 1;
  font-size: 18px;
  margin: 0;
}

.add-btn {
  background: #ff6600;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.main-content {
  padding: 16px;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.stat-card {
  background: white;
  padding: 12px;
  border-radius: 8px;
  text-align: center;
}

.stat-label {
  font-size: 12px;
  color: #999;
}

.stat-value {
  font-size: 20px;
  font-weight: bold;
  margin-top: 4px;
}

.stat-value.warning { color: #ff9900; }
.stat-value.danger { color: #ff4444; }

.filter-bar {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.filter-select, .filter-date {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.purchase-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.purchase-card {
  background: white;
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: box-shadow 0.2s;
}

.purchase-card:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.order-no {
  font-weight: bold;
  font-size: 14px;
}

.status-tag {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.status-tag.draft { background: #f0f0f0; color: #666; }
.status-tag.pending { background: #fff3e0; color: #ff9900; }
.status-tag.success { background: #e8f5e9; color: #4caf50; }
.status-tag.cancelled { background: #ffebee; color: #f44336; }

.card-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-row {
  display: flex;
  font-size: 14px;
}

.info-row .label {
  color: #999;
  width: 60px;
}

.info-row .value {
  flex: 1;
}

.info-row .money {
  color: #ff6600;
  font-weight: bold;
}

/* 商品列表样式 */
.products-list {
  color: #666;
  font-size: 13px;
  line-height: 1.4;
  word-break: break-all;
}

/* 紧凑行：金额、付款一行，日期单独一行 */
.compact-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #eee;
}

.compact-item {
  display: flex;
  align-items: center;
  font-size: 13px;
}

.compact-item .label {
  color: #999;
  margin-right: 4px;
}

.compact-item .value {
  white-space: nowrap;
}

.compact-item:last-child {
  width: 100%;
}

.unpaid { color: #f44336; }
.partial { color: #ff9900; }
.paid { color: #4caf50; }

.card-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #eee;
}

.action-btn {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 13px;
}

.action-btn.primary { background: #1976d2; color: white; border-color: #1976d2; }
.action-btn.success { background: #4caf50; color: white; border-color: #4caf50; }
.action-btn.warning { background: #ff9800; color: white; border-color: #ff9800; }
.action-btn.after-sale { background: #9c27b0; color: white; border-color: #9c27b0; }
.action-btn.danger { color: #f44336; border-color: #f44336; }

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
  padding: 16px;
}

.pagination button {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
}

.pagination button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
}

.modal-content h3 {
  margin: 0 0 16px 0;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 4px;
  font-size: 14px;
  color: #666;
}

.form-input, .form-select, .form-textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.money-display {
  font-size: 20px;
  font-weight: bold;
  color: #ff6600;
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
}

.btn-cancel, .btn-confirm {
  padding: 8px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-cancel {
  background: white;
  border: 1px solid #ddd;
}

.btn-confirm {
  background: #ff6600;
  color: white;
  border: none;
}

.proof-preview {
  max-width: 100%;
  max-height: 200px;
  margin-top: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

/* 退货弹窗样式 */
.modal-large {
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
}

.loading-state {
  text-align: center;
  padding: 40px;
  color: #999;
}

.return-section {
  margin-bottom: 16px;
}

.return-section h4 {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #333;
}

.return-items {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 4px;
}

.return-item {
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
}

.return-item:last-child {
  border-bottom: none;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
}

.checkbox-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.item-details {
  margin-top: 8px;
  padding-left: 24px;
}

.item-details .item-info {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #666;
  margin-bottom: 6px;
}

.item-details .return-quantity {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.item-details .return-quantity label {
  font-size: 13px;
  color: #666;
}

.item-details .quantity-input {
  width: 80px;
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
}

.item-details .item-subtotal {
  font-size: 13px;
  color: #ff6600;
  font-weight: bold;
}

.return-section .form-select,
.return-section .form-textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.return-section .form-textarea {
  resize: vertical;
}

.proof-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.proof-thumb {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.refund-info {
  background: #f9f9f9;
  border-radius: 4px;
  padding: 12px;
}

.refund-info .info-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 13px;
}

.refund-info .info-row.total {
  border-top: 1px solid #ddd;
  margin-top: 8px;
  padding-top: 8px;
  font-weight: bold;
}

.refund-info .info-row .amount {
  color: #ff6600;
  font-size: 16px;
}

.btn-confirm:disabled {
  background: #ccc;
  cursor: not-allowed;
}
</style>
